import { ledgerEvent, mergeSettings, planApply, recordKey, type LedgerEvent, type SyncedSettings, type SyncKind } from '@flow/core';
import type { FlowDb, HeadEntry } from './db';
import { deviceId, readSettings, uuid } from './dexieStores';

export type SyncState = 'off' | 'idle' | 'syncing' | 'offline' | 'error';

export interface SyncStatus {
  state: SyncState;
  /** Changes on this device not yet on the server. */
  pending: number;
  lastSyncAt: number | null;
  /** Why sync is paused (state 'error' or 'off'), in plain words. */
  message: string | null;
  /** Changes the server refused, with its reasons (most recent first). */
  rejected: RejectedChange[];
}

export interface RejectedChange { at: number; kind: string; recordId: string; message: string }

interface SyncResponse {
  stored: number;
  duplicates: number;
  rejected: { index: number; id: string | null; message: string }[];
  events: LedgerEvent[];
  cursor: number;
  more: boolean;
}

const BATCH = 200;
const TABLE: Record<Exclude<SyncKind, 'settings'>, 'tournaments' | 'rounds' | 'rows' | 'cells' | 'timers' | 'ballots'> = {
  tournament: 'tournaments', round: 'rounds', row: 'rows', cell: 'cells', timer: 'timers', ballot: 'ballots',
};

const getMeta = async <T,>(db: FlowDb, key: string, fallback: T): Promise<T> => ((await db.meta.get(key))?.value as T | undefined) ?? fallback;

/**
 * First sync on this database: queue a put for every record that has no event yet (data from before sync, or claimed
 * from the old shared database). Each is dated by its own last change, so merging two devices keeps the newer copy.
 */
export const seedOutbox = async (db: FlowDb, accountId: string) => {
  if (await getMeta(db, 'seeded', false)) return;
  await db.transaction('rw', db.everything, async () => {
    const known = new Set((await db.heads.toCollection().primaryKeys()) as string[]);
    const add = async (kind: SyncKind, data: { updatedAt?: number; createdAt?: number } & object) => {
      const at = data.updatedAt ?? data.createdAt ?? 1;
      const e = ledgerEvent({ accountId, deviceId: deviceId(), now: at, newId: uuid }, kind, { op: 'put', data });
      if (known.has(recordKey(kind, e.recordId))) return;
      await db.outbox.add(e);
      await db.heads.put({ key: recordKey(kind, e.recordId), at: e.at, id: e.id });
    };
    for (const kind of Object.keys(TABLE) as (keyof typeof TABLE)[]) {
      for (const rec of await db[TABLE[kind]].toArray()) await add(kind, rec);
    }
    // Settings carry no date: dated at the start of time, so any real change elsewhere wins.
    if (await db.settings.get('settings')) await add('settings', await readSettings(db));
    await db.meta.put({ key: 'seeded', value: true });
  });
};

/** Apply events from the server to this device's live tables (not recorded: they're already in the ledger). */
export const applyRemote = async (db: FlowDb, events: LedgerEvent[]) => {
  await db.transaction('rw', db.everything, async () => {
    const keys = events.map((e) => recordKey(e.kind, e.recordId));
    const heads = new Map((await db.heads.bulkGet(keys)).filter((h): h is HeadEntry => !!h).map((h) => [h.key, h]));
    for (const e of planApply(heads, events).apply) {
      if (e.kind === 'settings') {
        if (e.op === 'put') await db.settings.put(mergeSettings(await readSettings(db), e.data as SyncedSettings));
      } else {
        const table = db[TABLE[e.kind]] as unknown as { put(x: unknown): Promise<unknown>; delete(k: string): Promise<void> };
        if (e.op === 'put') await table.put(e.data);
        else await table.delete(e.recordId);
      }
      await db.heads.put({ key: recordKey(e.kind, e.recordId), at: e.at, id: e.id });
    }
  });
};

/**
 * Keeps this device's database in step with the account's ledger: sends queued changes, applies everyone else's.
 * Runs 2s after a change here; when the window gets focus, becomes visible or comes back online; and every 10s while
 * it's on screen (every minute in the background), so changes from another device show up within seconds.
 */
export const createSyncEngine = (opts: {
  db: FlowDb;
  accountId: string;
  /** Headers that authenticate the call, or null when signed out. */
  auth: () => Promise<Record<string, string> | null>;
  fetchImpl?: typeof fetch;
}) => {
  const { db, accountId, auth } = opts;
  const fetchImpl = opts.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  let status: SyncStatus = { state: 'idle', pending: 0, lastSyncAt: null, message: null, rejected: [] };
  const listeners = new Set<() => void>();
  const set = (patch: Partial<SyncStatus>) => {
    status = { ...status, ...patch };
    listeners.forEach((l) => l());
  };
  let running: Promise<void> | null = null;
  let again = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let failures = 0;
  let stopped = false;

  const refreshPending = async () => set({ pending: await db.outbox.count() });

  const once = async (): Promise<void> => {
    const headers = await auth();
    if (!headers) return set({ state: 'off', message: 'Sign in to sync.' });
    if (!navigator.onLine) return set({ state: 'offline', message: null });
    set({ state: 'syncing' });
    await seedOutbox(db, accountId);
    for (;;) {
      const batch = await db.outbox.orderBy('n').limit(BATCH).toArray();
      const since = await getMeta(db, 'cursor', 0);
      const res = await fetchImpl('/api/sync', {
        method: 'POST',
        headers: { ...headers, 'content-type': 'application/json' },
        body: JSON.stringify({ since, events: batch.map(({ n: _n, ...e }) => e) }),
      });
      if (res.status === 401 || res.status === 403) throw new SyncPaused(res.status === 403 ? "This account can't sync. Is it still invited?" : 'Sign in again to sync.');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as SyncResponse;
      const rejected: RejectedChange[] = body.rejected.map((r) => ({ at: Date.now(), kind: batch[r.index]?.kind ?? '?', recordId: batch[r.index]?.recordId ?? '?', message: r.message }));
      await db.transaction('rw', db.everything, async () => {
        await db.outbox.bulkDelete(batch.map((b) => b.n!));
        await applyRemote(db, body.events);
        await db.meta.put({ key: 'cursor', value: body.cursor });
        if (rejected.length) await db.meta.put({ key: 'rejected', value: [...rejected, ...(await getMeta<RejectedChange[]>(db, 'rejected', []))].slice(0, 50) });
      });
      const left = await db.outbox.count();
      set({ pending: left, rejected: await getMeta(db, 'rejected', []) });
      if (left === 0 && !body.more) break;
    }
    const now = Date.now();
    await db.meta.put({ key: 'lastSyncAt', value: now });
    failures = 0;
    set({ state: 'idle', lastSyncAt: now, message: null });
  };

  /** Sync now (or right after the sync in progress). */
  const run = (): Promise<void> => {
    if (stopped) return Promise.resolve();
    if (running) {
      again = true;
      return running;
    }
    running = once()
      .catch((e: unknown) => {
        failures++;
        if (e instanceof SyncPaused) set({ state: 'error', message: e.message });
        else if (!navigator.onLine) set({ state: 'offline', message: null });
        else set({ state: 'error', message: "Couldn't reach {flow}'s server. Your changes are safe here and will sync later." });
        // Back off: 5s, 10s, 20s… up to 5 minutes.
        schedule(Math.min(300_000, 5_000 * 2 ** (failures - 1)));
      })
      .finally(() => {
        running = null;
        void refreshPending();
        if (again) {
          again = false;
          void run();
        }
      });
    return running;
  };

  const schedule = (ms: number) => {
    if (stopped) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void run(), ms);
  };

  const onVisible = () => document.visibilityState === 'visible' && void run();
  const onWake = () => void run();
  let ticks = 0;
  const interval = setInterval(() => {
    ticks++;
    if (document.visibilityState === 'visible' || ticks % 6 === 0) void run();
  }, 10_000);
  window.addEventListener('online', onWake);
  window.addEventListener('focus', onWake);
  document.addEventListener('visibilitychange', onVisible);
  void (async () => set({ lastSyncAt: await getMeta<number | null>(db, 'lastSyncAt', null), rejected: await getMeta(db, 'rejected', []) }))();
  void refreshPending();

  return {
    getStatus: () => status,
    subscribe: (l: () => void) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    /** A local change was made: sync soon (changes in quick succession go together). */
    changed: () => {
      void refreshPending();
      schedule(2_000);
    },
    run,
    stop: () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      clearInterval(interval);
      window.removeEventListener('online', onWake);
      window.removeEventListener('focus', onWake);
      document.removeEventListener('visibilitychange', onVisible);
    },
  };
};

export type SyncEngine = ReturnType<typeof createSyncEngine>;

class SyncPaused extends Error {}
